import { Select, SelectProps } from '@cloudscape-design/components';
import { memo, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { Division } from 'src/types/orbit/Division';
import { convertList } from 'src/utils/generalUtils';

interface SingleSelectDivisionProps {
  divisions: Division[];
  isLoading: boolean;
  readOnly?: boolean;
  error: string | null;
  selectedDivision: string | null;
  onItemSelection: (selectedDivision: Division | null) => void;
  /** Placeholder shown when no division is selected. Defaults to "Choose a division". */
  placeholder?: string;
}

const SingleSelectDivision = ({
  divisions,
  error,
  isLoading,
  selectedDivision,
  readOnly,
  onItemSelection,
  placeholder = 'Choose a division',
}: SingleSelectDivisionProps) => {
  const [selected, setSelected] = useState<SelectProps.Option | null>(null);

  useEffect(() => {
    if (selectedDivision) {
      const division = divisions.find((data) => data.divisionName === selectedDivision);
      if (division) {
        setSelected({
          value: division.divisionName,
          label: division.divisionName,
        });
      }
    } else {
      setSelected(null);
    }
  }, [divisions, selectedDivision]);

  // Memoize the status type calculation
  const statusType = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    return 'finished';
  }, [isLoading, error]);

  // Memoize the divisions list conversion
  const divisionOptions = useMemo(() => {
    return convertList<Division, SelectProps.Option>(divisions, (item) => ({
      value: item.divisionName,
      label: item.divisionName,
    }));
  }, [divisions]);

  return (
    <Select
      readOnly={readOnly}
      filteringType="auto"
      data-testid="division-select"
      selectedOption={selected}
      onChange={({ detail }) => {
        const value = detail.selectedOption;
        setSelected(value);
        if (value) {
          const selectedDiv = divisions.find((div) => div.divisionName === value.value);
          onItemSelection(selectedDiv || null);
        } else {
          onItemSelection(null);
        }
      }}
      options={divisionOptions}
      errorText={error || undefined}
      loadingText="Loading divisions"
      statusType={statusType}
      placeholder={placeholder}
    />
  );
};

// Memoize the entire component
export default memo(SingleSelectDivision, (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
});
